import { TemplateVariable, SamplePerson, samplePerson } from "./components/template-variables";

/**
 * What each Nok email template type may use, and what the preview fills it with.
 *
 * Every type used to be shown the email wrapper's three variables, so a Compliance Sniffer body
 * listed `{{email_content}}`, which means nothing there, and none of the three it really uses.
 * Each list here is what HQ-API substitutes for that type, checked against the code:
 *
 * - `email_wrapper`: `NokEmailComposer` and the Mystery Shopper send.
 * - `cs_email_body`: `ComplianceSnifferBodyRenderer`. NN's composer preview fills the same three.
 * - `ms_email_body`: `{{to_name}}`, filled by `MysteryShopperCustomerActivationProcessingHandler`.
 *   NN's editor shows the active recipient's name in its place.
 *
 * The form types (`ms_form_message`, `cs_form_message`) use a plain text box, not this editor.
 */
export const NOK_TEMPLATE_VARIABLES: Record<string, TemplateVariable[]> = {
    email_wrapper: [
        { token: '{{nok_name}}', description: "The name of the Nok website the email is sent from." },
        { token: '{{email_content}}', description: 'The body of the email, as written in the composer.' },
        { token: '{{email_signature}}', description: "The sending persona's signature, as the Nok website configures it." },
    ],
    cs_email_body: [
        { token: '{{to_name}}', description: 'The name of the contact the email is sent to. A test email greets the customer sending the test.' },
        { token: '{{compliance_noks}}', description: 'A bulleted list of the Noks selected for the sniff. Put it on its own line, not inside a paragraph.' },
        { token: '{{signature}}', description: 'The compliance signature (the default_compliance_sniffer_email_signature setting), filled with the sender\'s details.' },
    ],
    ms_email_body: [
        { token: '{{to_name}}', description: 'The name of the contact the email is sent to. A test email greets the customer sending the test.' },
    ],
};

/** The variables for a template type; none for a type HQ-API fills nothing in. */
export function variablesForType(type: string | undefined): TemplateVariable[] {
    return NOK_TEMPLATE_VARIABLES[type ?? ''] ?? [];
}

/**
 * The Nok list exactly as HQ-API sends it (`ComplianceSnifferBodyRenderer::nokList`), so the
 * preview shows the real spacing and bullets.
 */
export function sampleNokList(noks: { name: string, description: string }[]): string {
    const items: string = noks.map((nok: { name: string, description: string }): string =>
        '<li style="margin:0 0 8px;"><strong>' + nok.name + '</strong>'
        + (nok.description !== '' ? '<br><span style="font-size:13px;color:#6b7280;">' + nok.description + '</span>' : '')
        + '</li>'
    ).join('');

    return '<ul style="margin:0 0 16px;padding-left:20px;list-style:disc;font-size:14px;line-height:1.5;">' + items + '</ul>';
}

/**
 * The compliance signature with sample sender details, the way HQ-API fills it.
 *
 * @param signatureHtml the stored setting, or undefined while it loads
 */
export function sampleComplianceSignature(signatureHtml: string | undefined, hostUrl: string, person: SamplePerson): string {
    if (typeof signatureHtml !== 'string' || signatureHtml === '') {
        return '<p><em>[Compliance signature]</em></p>';
    }
    const replacements: Record<string, string> = {
        '{{nok_company_logo}}': '<img src="' + hostUrl + 'images/admin/nok/nn-logo-white.png" alt="NokNok" style="width: 100%;height:auto;max-width: 100px;"/>',
        '{{tester_name}}': person.name,
        '{{tester_email}}': person.email,
        '{{nok_url}}': 'https://noknok.ai/',
    };

    return signatureHtml.replace(
        /\{\{(?:nok_company_logo|tester_name|tester_email|nok_url)\}\}/g,
        (token: string): string => replacements[token] ?? token
    );
}

/** The wrapper's persona signature, as the old preview drew it. */
function samplePersonaSignature(hostUrl: string, person: SamplePerson): string {
    return `
        <table cellpadding="0" cellspacing="0" border="0" style="margin-top: 30px;width:100%;padding-bottom:30px">
            <tbody>
                <tr>
                    <td style="vertical-align: top; padding-right: 15px;width:10%;">
                        <span style="border-radius: 50%;width:80px;height:80px;overflow:hidden;display:block;margin-left:5px;">
                            <img src="${person.image}" alt="${person.name}" style="width: 100%;height:auto;max-width: 100%;">
                        </span>
                    </td>
                    <td style="vertical-align: top;width:65%">
                        <p style="margin: 0; font-weight: bold; font-size: 16px; color: #2c3e50;"><span>${person.name}</span></p>
                        <p style="margin: 0; font-size: 14px; color: #555;"><span>Nok Persona</span></p>
                        <p style="margin: 5px 0; font-size: 14px; color: #555;">
                            <span style="color: #1a73e8;"><a href="mailto:${person.email}" style="color:inherit;text-decoration:inherit">${person.email}</a></span><br>
                            <span style="color: #1a73e8;"><a href="tel:00000000001" style="color:inherit;text-decoration:inherit">00000 000001</a></span><br>
                            <span style="color: #1a73e8;"><a href="https://noknok.ai" target="_blank" style="color:inherit;text-decoration:inherit">https://noknok.ai</a></span>
                        </p>
                    </td>
                    <td style="vertical-align: top; padding-left: 15px;padding-right:15px">
                        <span style="width:80px;height:80px;">
                            <img src="${hostUrl}images/admin/nok/nn-logo-white.png" alt="Nok Persona" style="width: 100%;height:auto;max-width: 100%;">
                        </span>
                    </td>
                </tr>
            </tbody>
        </table>
    `;
}

/**
 * Sample values for the preview of one template type. Rebuilt per preview so one person is used
 * throughout.
 *
 * @param complianceSignature the stored compliance signature, for `cs_email_body`
 */
export function buildNokTemplateSamples(
    type: string | undefined,
    hostUrl: string,
    complianceSignature?: string
): Record<string, string> {
    const person: SamplePerson = samplePerson(hostUrl);
    if (type === 'cs_email_body') {
        return {
            '{{to_name}}': 'Sam Newman',
            '{{compliance_noks}}': sampleNokList([
                { name: 'DKIM signature present', description: 'Checks the email is signed by the sending domain.' },
                { name: 'Detect GDPR/Privacy statement', description: 'Looks for a privacy statement in the reply.' },
                { name: 'Privacy policy URL is live', description: 'Confirms the privacy policy link loads.' },
            ]),
            '{{signature}}': sampleComplianceSignature(complianceSignature, hostUrl, person),
        };
    }
    if (type === 'ms_email_body') {
        return { '{{to_name}}': 'Sam Newman' };
    }
    if (type === 'email_wrapper') {
        return {
            '{{nok_name}}': 'Nok Nok Ltd',
            '{{email_content}}': `
                <p>Hello Lorem,</p>
                <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam egestas vitae eros non cursus. Suspendisse potenti. Donec at magna est. Morbi sit amet molestie diam.</p>
                <p>Curabitur eget velit sed nunc tempus faucibus. Integer risus diam, euismod eget leo eget, euismod cursus metus.</p>
            `,
            '{{email_signature}}': samplePersonaSignature(hostUrl, person),
        };
    }

    return {};
}
